import { CloseOutlined } from '@ant-design/icons';
import { useClickOutside } from '@mm/hooks';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { assertUnreachable } from '@mm/utils';
import { ReactComponent as InstagramIcon } from 'assets/instagram.svg';
import { ReactComponent as FBIcon } from 'assets/messenger-fb-new.svg';
import { ReactComponent as TriggersIcon } from './icons/triggers-icon.svg';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useImportContactsPermission } from '../../../../../../../common/hooks/use-import-contacts-permission';
import { ReactComponent as GmailIcon } from '../../AutomationHome/TemplateIndex/gmail.svg';
import { InputTriggerModal } from './InputTriggerModal/InputTriggerModal';
import styles from './NewInputTriggerChooser.module.css';
import { TriggerCardDefinition } from './types';
import {
  CATEGORIES,
  Category,
  CATEGORY_NAMES,
  disabledInputTriggerProps,
  TemplateParams,
  TRIGGER_DEFINITIONS,
  TRIGGERS_BY_CATEGORY,
  TriggerType,
} from './TriggersDefinitions';
import {
  ConnectableChannel,
  useChannelConnection,
} from './use-channel-connection';
import { useDisabledInputTriggers } from './use-disabled-input-triggers';
import { useInputTriggerCRUD } from './use-input-trigger-crud';
import { useInputTriggersIdentification } from './use-input-triggers-identification';
import { AccordionItem } from '@mm/components';
import { Tooltip } from 'antd';

interface Props {
  className?: string;
  campaignId: number;
  onClose: () => void;
  triggers: ReturnType<typeof useInputTriggersIdentification>;
  setAddingInputTrigger?: (ok: boolean) => void;
}

const colors = {
  blueGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #F7F0EF 15.24%, #CEE1FF 50%, #85B4EE 100%)',
  pinkGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #FFFAE4 4.17%, #FEC6E3 52.6%, #BFA8FF 100%)',
  limeGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #FEEFC7 5.58%, #D0F0D9 44.46%, #86C7C3 100%)',
  cyanGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #F3F7EF 4.17%, #A4E1DE 52.6%, #4BB4EF 100%)',
};

export const CONNECTABLE_CHANNEL_CARD_DEFINITIONS: Record<
  ConnectableChannel,
  TriggerCardDefinition
> = {
  facebook: {
    connectableChannelType: 'facebook',
    icon: <FBIcon width={38} height={38} />,
    name: 'Connect Facebook',
    nameComponent: (
      <PrimaryButton className={styles.connectChannelBtn}>
        Connect Facebook
      </PrimaryButton>
    ),
    description: 'and start generating more leads now!',
    descriptionClassName: styles.description,
    color: colors.blueGradient,
    hideAddButton: true,
    template: false,
  },
  instagram: {
    connectableChannelType: 'instagram',
    icon: <InstagramIcon width={38} height={38} />,
    name: 'Connect Instagram',
    nameComponent: (
      <PrimaryButton className={styles.connectChannelBtn}>
        Connect Instagram
      </PrimaryButton>
    ),
    description: 'and start generating more leads now!',
    descriptionClassName: styles.description,
    color: colors.pinkGradient,
    hideAddButton: true,
    template: false,
  },
  email: {
    connectableChannelType: 'email',
    icon: <GmailIcon width={38} height={38} />,
    name: 'Connect Gmail',
    nameComponent: (
      <PrimaryButton className={styles.connectChannelBtn}>
        Connect Gmail
      </PrimaryButton>
    ),
    description: 'and start re-engaging your leads with email!',
    descriptionClassName: styles.description,
    color: colors.cyanGradient,
    hideAddButton: true,
    template: false,
  },
};

const CONNECTABLE_CHANNELS_BY_CATGEGORY: Record<
  Category,
  null | ConnectableChannel[]
> = {
  featured: ['email'],
  facebook: ['facebook'],
  instagram: ['instagram'],
  webchat: [],
};

export const NewInputTriggerChooser = (props: Props) => {
  const { className, campaignId, onClose, triggers, setAddingInputTrigger } =
    props;

  const disabledInputTriggers = useDisabledInputTriggers({
    onLinkClick: onClose,
  });

  const { createInputTriggerAndOpenSlideout } = useInputTriggerCRUD();

  const {
    isSMSConnected,
    isInstagramConnected,
    isFacebookConnected,
    isEmailConnected,
    connectChannel,
  } = useChannelConnection(() => setAddingInputTrigger?.(true));
  const ref = useClickOutside<HTMLInputElement>(onClose);
  const campaign = useSelector((state: StoreState) =>
    state.campaigns.entities.find(c => c.id === campaignId),
  );
  const enabledInputTriggers = useSelector(
    (state: StoreState) => state.campaignsOverview.enabledInputTriggers,
  );
  const featureFlags = useSelector(combinedFeatureFlagsSelector);

  const [selectedTriggerCard, setSelectedTriggerCard] =
    useState<TriggerCardDefinition>();

  const importPermissions = useImportContactsPermission();

  const getCardsForCategory = (categoryName: string) => {
    const category = TRIGGERS_BY_CATEGORY[categoryName];
    if (!category.length) {
      return null;
    }

    const hideConnectedChannels = (
      connectableChannelType: ConnectableChannel,
    ) => {
      switch (connectableChannelType) {
        case 'instagram':
          return !isInstagramConnected;
        case 'facebook':
          return !isFacebookConnected;
        case 'email':
          return !isEmailConnected;
        default:
          return assertUnreachable(connectableChannelType);
      }
    };

    const connectCards: TriggerCardDefinition[] =
      CONNECTABLE_CHANNELS_BY_CATGEGORY[categoryName]
        .filter(hideConnectedChannels)
        .map(connectableChannelType => ({
          ...CONNECTABLE_CHANNEL_CARD_DEFINITIONS[connectableChannelType],
        }));

    const hideByFeatureFlag = (triggerCard: TriggerCardDefinition) =>
      !triggerCard.requiredFeature || featureFlags[triggerCard.requiredFeature];

    const triggerCards = category
      .map<TriggerCardDefinition>(triggerType => ({
        ...TRIGGER_DEFINITIONS[triggerType],
        ...disabledInputTriggerProps({
          disabledInputTriggers,
          triggers,
          triggerType,
          isFacebookConnected,
          isInstagramConnected,
          isSMSConnected,
          isAllowedToImportContacts: importPermissions.isAllowed,
          isEmailConnected,
        }),
        triggerType,
        icon: React.createElement(TRIGGER_DEFINITIONS[triggerType].icon),
      }))
      .filter(hideByFeatureFlag);

    const byName = (a, b) => a.name.localeCompare(b.name);
    const featuredCards = triggerCards.filter(x => x.featured);
    const sortedTriggerCards = triggerCards
      .filter(x => !x.featured)
      .sort(byName);

    return [...connectCards, ...featuredCards, ...sortedTriggerCards].filter(
      trigger =>
        !enabledInputTriggers?.find(input => input === trigger.triggerType),
    );
  };

  const onTriggerClick = useCallback(
    (triggerCard: TriggerCardDefinition) => {
      if (triggerCard.disabled) {
        return;
      }

      if (triggerCard.connectableChannelType) {
        connectChannel(triggerCard.connectableChannelType);
        onClose();
        return;
      }

      if (!triggerCard.template && triggerCard.triggerType) {
        if (!campaign) {
          return;
        }
        createInputTriggerAndOpenSlideout(campaign, triggerCard.triggerType);
        onClose();
        return;
      }

      setSelectedTriggerCard(triggerCard);
    },
    [campaign, connectChannel, createInputTriggerAndOpenSlideout, onClose],
  );

  if (!campaign) {
    return null;
  }
  const onTemplateParamsSubmitted = async (
    triggerType: TriggerType,
    params: TemplateParams,
  ) => {
    await createInputTriggerAndOpenSlideout(campaign, triggerType, params);
    onClose();
  };

  const TriggerList = (
    <div className={classNames(className, styles.container)}>
      <div className={styles.heading}>
        <div className={styles.headingText}>
          <TriggersIcon /> Triggers
        </div>
        <span className={styles.closeButton} onClick={onClose}>
          <CloseOutlined />
        </span>
      </div>

      <div className={styles.categories}>
        {CATEGORIES.map(categoryName => {
          const triggersByCategory = getCardsForCategory(categoryName);
          const isFeatured = categoryName === 'featured';
          return (
            <AccordionItem
              className={styles.accordion}
              title={CATEGORY_NAMES[categoryName]}
              key={categoryName}
              canToggle={!isFeatured}
              showArrow={!isFeatured}
              openOnMount={isFeatured}
              withBorder={false}
              closeOnOutsideClick={false}
            >
              <div
                className={classNames(styles.triggerList, {
                  [styles.featuredTriggersList]: isFeatured,
                })}
              >
                {triggersByCategory &&
                  triggersByCategory.map(card => (
                    <div
                      key={card.triggerType || card.connectableChannelType}
                      className={classNames(styles.trigger, {
                        [styles.disabled]: card.disabled,
                      })}
                    >
                      <Tooltip
                        title={card.disabledTooltip}
                        getTooltipContainer={trigger =>
                          trigger.parentNode as HTMLElement
                        }
                        placement="bottom"
                      >
                        <div
                          className={classNames(styles.card, {
                            [styles.featuredCard]: isFeatured,
                          })}
                          onClick={() => onTriggerClick(card)}
                        >
                          <div
                            className={classNames(styles.triggerIcon, {
                              [styles.featuredTriggerIcon]: isFeatured,
                            })}
                          >
                            {card.icon}
                          </div>
                          <div className={styles.triggerInfo}>
                            <div
                              className={classNames(styles.triggerName, {
                                [styles.featuredTriggerName]: isFeatured,
                              })}
                            >
                              {card.nameComponent || card.name}
                            </div>
                            {!isFeatured && (
                              <div
                                className={classNames(
                                  styles.triggerDescription,
                                  card.descriptionClassName,
                                )}
                              >
                                {card.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </Tooltip>
                    </div>
                  ))}
              </div>
            </AccordionItem>
          );
        })}
      </div>
    </div>
  );

  const TriggerTemplate = selectedTriggerCard?.triggerType && (
    <InputTriggerModal
      campaignId={campaignId}
      triggerType={selectedTriggerCard.triggerType}
      onSubmit={onTemplateParamsSubmitted}
      onBack={() => setSelectedTriggerCard(undefined)}
      onClose={onClose}
    />
  );

  return (
    <div ref={ref} className={styles.modalContainer}>
      {selectedTriggerCard ? TriggerTemplate : TriggerList}
    </div>
  );
};
